import _isEqual from 'underscore/cjs/isEqual.js'
import RewardsService from '~/services/rewards.service'
import memberhubConstants from '~/constants/memberhub.constants'
const rewardsApiService = new RewardsService()

const actions = {
  loader (status = true) {
    const layoutStore = useLayoutsStore()
    layoutStore.setPageLoader(status)
  },
  updateReward (reward) {
    const currentRewardIndex = this.rewards.findIndex(rewardItem => rewardItem.id === reward.id)
    if (currentRewardIndex > -1 && this.rewards[currentRewardIndex]) {
      this.rewards[currentRewardIndex].status = reward.status
      if (reward.claim_url_link) {
        this.rewards[currentRewardIndex].claim_url_link = reward.claim_url_link
      }
      if (reward.date_activated) {
        this.rewards[currentRewardIndex].date_activated = reward.date_activated
      }
    }
  },
  /*
   * Rewards fetching store action (stale-while-revalidate).
   * Bumps `rewards_revision` only when the payload actually changed. On error a
   * cached list is kept (stale) instead of being wiped; only a cold-start failure
   * clears state. Resolves with the current list either way so the page mount
   * flow never dies on a transient refresh failure. The wallet listing reacts
   * to the store state directly (see the `walletCards` getter).
   * @returns {Promise<unknown>}
   */
  async rewardsList () {
    try {
      const r = await rewardsApiService.list()
      const response = await checkResponse(r, false)
      const next = response.data || []
      if (!_isEqual(this.rewards, next)) {
        this.rewards = next
        this.rewards_revision++
      }
      this.rewards_fetched_at = Date.now()
      const hashId = useUserStore().getUserProfile?.hash_id
      if (hashId) {
        this.rewards_user_id = hashId
      }
      return this.rewards
    } catch (error) {
      if (this.rewards.length > 0) {
        // eslint-disable-next-line no-console -- diagnostic for ops
        console.warn('[rewardsStore] rewardsList failed, keeping cached rewards', error?.message || error)
        return this.rewards
      }
      // eslint-disable-next-line no-console -- diagnostic for ops
      console.warn('[rewardsStore] rewardsList failed with empty cache', error?.message || error)
      this.rewards = []
      return this.rewards
    }
  },
  /*
   * Shared reward-mutation flow: toggle the page loader, call the service, and
   * apply the returned status/fields to the cached reward. `buildPatch(response)`
   * returns the per-action fields to merge; the mutated state drives the wallet
   * listing via the `walletCards` getter.
   * @param {string} serviceMethod - RewardsService method (activate|archive|unArchive)
   * @param {object} currentReward
   * @param {(response: object) => object} buildPatch
   * @returns {Promise<unknown>}
   */
  async _mutateReward (serviceMethod, currentReward, buildPatch) {
    this.loader(true)
    const r = await rewardsApiService[serviceMethod](currentReward?.id)
    return checkResponse(r, true)
      .then(response => {
        this.updateReward({ id: currentReward.id, ...buildPatch(response) })
        return response.data
      })
      .finally(() => {
        this.loader(false)
      })
  },
  /*
   * Activating a reward store action
   * @param currentReward
   * @returns {Promise<unknown>}
   */
  activateReward (currentReward) {
    return this._mutateReward('activate', currentReward, response => ({
      status: memberhubConstants.GIFT_CARD_STATUS.ACTIVATED,
      claim_url_link: response.data.gc_url,
      date_activated: response.data.date_activated
    }))
  },
  /*
   * Archive reward store action
   * @param currentReward
   * @returns {Promise<unknown>}
   */
  archiveReward (currentReward) {
    return this._mutateReward('archive', currentReward, () => ({
      status: memberhubConstants.GIFT_CARD_STATUS.ARCHIVED
    }))
  },
  /*
   * Un Archive reward store action
   * @param currentReward
   * @returns {Promise<unknown>}
   */
  unArchiveReward (currentReward) {
    return this._mutateReward('unArchive', currentReward, () => ({
      status: memberhubConstants.GIFT_CARD_STATUS.ACTIVATED
    }))
  }
}
export default actions
